import React, { useEffect, useMemo, useState } from "react";
import { Users, MessageCircle, Star, Phone, Mail, Bot } from "lucide-react";
import { api } from "../../lib/api";

const levelStyle = (level) => {
  if (level === "Alto") return "bg-green-500/15 text-green-400 border-green-500/30";
  if (level === "Medio") return "bg-yellow-400/15 text-yellow-impact border-yellow-400/30";
  if (level === "Bajo") return "bg-zinc-500/15 text-zinc-400 border-zinc-500/30";
  return "bg-purple-500/15 text-purple-300 border-purple-500/30";
};

const Transcript = ({ transcript, leadName }) => {
  const lines = useMemo(() => transcript.split("\n").filter((line) => line.trim()), [transcript]);
  return (
    <div className="mt-2 space-y-2 border-l-2 border-purple-500/30 pl-3 max-h-72 overflow-y-auto">
      {lines.map((line, i) => {
        const isLead = line.startsWith("Prospecto:");
        const text = line.replace(/^(Prospecto:|Asesor IA:)\s*/, "");
        return (
          <div key={`t-${i}`} className="text-xs">
            <span className={isLead ? "text-yellow-impact font-semibold" : "text-purple-300 font-semibold"}>
              {isLead ? `${leadName?.split(" ")[0] || "Prospecto"}: ` : "Asesor IA: "}
            </span>
            <span className="text-zinc-300">{text}</span>
          </div>
        );
      })}
    </div>
  );
};


export default function Leads() {
  const [leads, setLeads] = useState(null);

  const load = () => api.get("/leads").then((r) => setLeads(r.data)).catch(() => setLeads([]));
  useEffect(() => {
    load();
    const id = setInterval(load, 8000); // refresh to catch AI qualification
    return () => clearInterval(id);
  }, []);

  return (
    <div data-testid="leads-page">
      <div className="flex items-center gap-3 mb-2">
        <Users className="text-purple-500" size={26} />
        <h1 className="font-display font-black tracking-tight text-3xl">Prospectos</h1>
      </div>
      <p className="text-zinc-400 mb-8">La IA califica cada prospecto. Los <span className="text-green-400 font-semibold">Altos</span> son tu prioridad.</p>

      {leads === null && <p className="text-zinc-500">Cargando...</p>}
      {leads?.length === 0 && (
        <div className="rounded-2xl border border-white/10 bg-[#121212] p-10 text-center">
          <Users className="mx-auto text-zinc-600 mb-3" size={32} />
          <p className="text-zinc-400">Aún no hay prospectos. Comparte el enlace de tus <span className="text-purple-400">Embudos</span>.</p>
        </div>
      )}

      <div className="space-y-4">
        {leads?.map((l) => (
          <div key={l.lead_id} data-testid="lead-card" className="rounded-2xl border border-white/10 bg-[#121212] p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <h3 className="font-display font-bold text-lg">{l.name}</h3>
                <span className={`text-xs px-2.5 py-1 rounded-full border font-semibold ${levelStyle(l.level)}`}>
                  {l.score != null ? `${l.level} · ${l.score}/100` : l.level}
                </span>
                {l.source === "chat_ai" && (
                  <span data-testid="lead-source-chat" className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border border-purple-500/30 bg-purple-500/15 text-purple-300 font-semibold">
                    <Bot size={12} /> Charla IA
                  </span>
                )}
                {l.qualified && <span className="inline-flex items-center gap-1 text-xs text-yellow-impact font-bold"><Star size={12} /> Calificado</span>}
              </div>
              <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2 text-sm text-zinc-400">
                {l.phone && <span className="flex items-center gap-1.5"><Phone size={14} /> {l.phone}</span>}
                {l.email && <span className="flex items-center gap-1.5"><Mail size={14} /> {l.email}</span>}
              </div>
              {l.message && <p className="text-sm text-zinc-500 mt-2 italic">"{l.message}"</p>}
              {l.answers?.length > 0 && (
                <details data-testid="lead-answers" className="mt-2">
                  <summary className="text-xs text-purple-400 cursor-pointer hover:text-purple-300">Ver respuestas del cuestionario ({l.answers.length})</summary>
                  <div className="mt-2 space-y-1.5 border-l-2 border-purple-500/30 pl-3">
                    {l.answers.map((a, i) => (
                      <div key={`${a.question}-${i}`} className="text-xs">
                        <span className="text-zinc-500">{a.question}</span>
                        <div className="text-zinc-300">→ {a.answer || "—"}</div>
                      </div>
                    ))}
                  </div>
                </details>
              )}
              {l.chat_transcript && (
                <details data-testid="lead-transcript" className="mt-2">
                  <summary className="text-xs text-purple-400 cursor-pointer hover:text-purple-300 inline-flex items-center gap-1.5"><Bot size={13} /> Ver conversación con la IA</summary>
                  <Transcript transcript={l.chat_transcript} leadName={l.name} />
                </details>
              )}
              {l.reason && <p className="text-xs text-zinc-600 mt-1">IA: {l.reason}</p>}
              <p className="text-xs text-zinc-600 mt-1">Embudo: {l.funnel_title} · {new Date(l.created_at).toLocaleDateString("es-MX")}</p>
            </div>
            {l.wa_link && (
              <a
                data-testid="contact-whatsapp"
                href={l.wa_link}
                target="_blank"
                rel="noreferrer"
                className="shrink-0 inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-500 text-white font-bold rounded-xl px-5 py-3 text-sm"
              >
                <MessageCircle size={18} /> Contactar por WhatsApp
              </a>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
